import React, { useState } from 'react';
import Window from '../os/Window';

const FOLDER_FILE_TREE = [
    { name: 'src', type: 'folder' as const, open: true, children: [
        { name: 'components', type: 'folder' as const, open: true, children: [
            { name: 'os', type: 'folder' as const, open: true, children: [
                { name: 'Desktop.tsx', type: 'file' as const },
                { name: 'Window.tsx', type: 'file' as const },
            ]},
        ]},
        { name: 'App.tsx', type: 'file' as const },
    ]},
    { name: 'package.json', type: 'file' as const },
];

const TERMINAL_RUNNING_LINES = [
    '$ npm run dev',
    '',
    '> portfolio@1.0.0 dev',
    '> vite',
    '',
    '  VITE v5.x  ready in 320 ms',
    '',
    '  ➜  Local:   http://localhost:3000/',
    '',
];

type TreeItem = { name: string; type: 'folder' | 'file'; open?: boolean; children?: TreeItem[] };

const treeRowBlock: React.CSSProperties = { display: 'block', width: '100%', boxSizing: 'border-box' };

function renderTree(items: TreeItem[], depth = 0): React.ReactNode {
    const indent = 4;
    const chevron = (open: boolean) => open ? '\u25BC' : '\u203A';
    return items.map((item) => {
        if (item.type === 'folder') {
            const isOpen = item.open === true;
            return (
                <div key={item.name} style={{ ...treeRowBlock, marginLeft: depth * indent }}>
                    <div style={{ display: 'flex', alignItems: 'center', padding: '2px 0', color: '#9cdcfe', fontSize: 12, fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' }}>
                        <span style={{ width: 12, fontSize: 10, color: 'rgba(255,255,255,0.7)', marginRight: 2 }}>{chevron(isOpen)}</span>
                        <svg width="12" height="10" viewBox="0 0 14 12" fill="none" style={{ flexShrink: 0, marginRight: 6 }}><path d="M0 1a1 1 0 011-1h4l2 2h6a1 1 0 011 1v7a1 1 0 01-1 1H1a1 1 0 01-1-1V1z" fill="#8B7355" stroke="#5D4E37" strokeWidth="0.8" /></svg>
                        <span>{item.name}</span>
                    </div>
                    {item.children && isOpen && (
                        <div style={{ ...treeRowBlock, marginLeft: 2 }}>{renderTree(item.children, depth + 1)}</div>
                    )}
                </div>
            );
        }
        return (
            <div key={item.name} style={{ ...treeRowBlock, display: 'flex', alignItems: 'center', marginLeft: depth * indent + 2, padding: '2px 0', fontSize: 12, color: '#ce9178', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' }}>
                <span style={{ width: 12, opacity: 0, marginRight: 2 }}>{'\u203A'}</span>
                <svg width="10" height="12" viewBox="0 0 12 14" fill="none" style={{ flexShrink: 0, marginRight: 6 }}><path d="M1 0a1 1 0 00-1 1v12a1 1 0 001 1h10a1 1 0 001-1V4L8 0H1z" fill="#2D2D2D" stroke="#555" strokeWidth="0.8" /><path d="M8 0v4h4" stroke="#555" strokeWidth="0.8" fill="none" /></svg>
                <span>{item.name}</span>
            </div>
        );
    });
}

/**
 * Cursor-style IDE window for the Cursor app: sidebar, main area (Portfolio running), terminal running, chat.
 * Matches loader layout. Close terminal button refreshes the page.
 */
const CursorAppWindow: React.FC<WindowAppProps> = (props) => {
    const [terminalVisible, setTerminalVisible] = useState(true);

    const closeTerminal = () => {
        setTerminalVisible(false);
        window.location.reload();
    };

    return (
        <Window
            top={80}
            left={80}
            width={920}
            height={620}
            windowTitle="Cursor - portfolio-website"
            windowBarIcon="cursorIcon"
            closeWindow={props.onClose}
            onInteract={props.onInteract}
            minimizeWindow={props.onMinimize}
        >
            <div style={styles.wrapper}>
                <div style={styles.body}>
                    <div style={styles.activityBar}>
                        <div style={styles.activityIconActive} title="Explorer">{'\u2630'}</div>
                        <div style={styles.activityIcon} title="Search">{'\u2315'}</div>
                        <div style={styles.activityIcon} title="Source Control">{'\u2442'}</div>
                        <div style={styles.activityIcon} title="Extensions">{'\u25A1'}</div>
                    </div>
                    <div style={styles.sidebar}>
                        <div style={styles.sidebarTabsRow}>
                            <span style={styles.sidebarTabActive}>FILES</span>
                            <span style={styles.sidebarTabInactive}>OUTLINE</span>
                        </div>
                        <div style={styles.fileTree}>
                            <div style={styles.fileTreeInner}>{renderTree(FOLDER_FILE_TREE)}</div>
                        </div>
                    </div>
                    <div style={styles.mainArea}>
                        <div style={styles.tabsRow}>
                            <div style={styles.tabActive}>
                                <span>Desktop.tsx</span>
                                <span style={styles.tabClose}>×</span>
                            </div>
                            <div style={styles.tabInactive}>
                                <span>App.tsx</span>
                                <span style={styles.tabClose}>×</span>
                            </div>
                        </div>
                        <div style={styles.contentPanel}>
                            <div style={styles.staticContent}>
                                Portfolio running. You can launch your simulation whenever you're ready.
                            </div>
                        </div>
                        {terminalVisible && (
                            <div style={styles.terminalPanel}>
                                <div style={styles.terminalHeader}>
                                    <span>TERMINAL</span>
                                    <span style={styles.terminalShellLabel}>zsh</span>
                                    <div style={styles.terminalHeaderRight}>
                                        <button type="button" style={styles.terminalHeaderBtn} aria-label="New terminal">+</button>
                                        <button type="button" style={styles.terminalHeaderBtn} aria-label="Split">{'\u229E'}</button>
                                        <button
                                            type="button"
                                            style={styles.terminalHeaderBtnClose}
                                            aria-label="Close terminal (refreshes page)"
                                            title="Close terminal (refreshes page)"
                                            onClick={closeTerminal}
                                        >
                                            <span style={styles.terminalCloseIcon}>{'\u2715'}</span>
                                        </button>
                                    </div>
                                </div>
                                <div style={styles.terminalContent}>
                                    <div style={styles.terminalLines}>
                                        {TERMINAL_RUNNING_LINES.map((line, i) => (
                                            <div key={i} style={styles.terminalLine}>{line}</div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                    <div style={styles.chatPanel}>
                        <div style={styles.chatTopRow}>
                            <div style={styles.chatTabs}>
                                <span style={styles.chatTabActive}>Portfolio</span>
                                <span style={styles.chatTabInactive}>History</span>
                            </div>
                            <div style={styles.chatTopRight}>
                                <button type="button" style={styles.chatTopBtn}>+</button>
                                <span style={styles.chatTopIcon} title="History">{'\u2316'}</span>
                                <button type="button" style={styles.chatTopBtn} title="More">{'\u22EE'}</button>
                            </div>
                        </div>
                        <div style={styles.chatMessages}>
                            <div style={styles.chatBubble}>
                                <div style={styles.chatBubbleContent}>
                                    <div style={styles.chatMessage}>
                                        Dev server is running (npm run dev). You can use the portfolio in this window.
                                    </div>
                                </div>
                            </div>
                            <p style={styles.chatEasterEgg}>Close the terminal if you wish to stop running the app.</p>
                        </div>
                        <div style={styles.reviewTextRow}>
                            <span style={styles.reviewTextItem}>Undo</span>
                            <span style={styles.reviewTextItem}>Keep</span>
                            <span style={styles.reviewTextItem}>Review</span>
                        </div>
                        <div style={styles.chatPromptContainer}>
                            <div style={styles.chatPromptTop}>
                                <span style={styles.chatModePillActive}>Agent</span>
                                <span style={styles.chatModePill}>Auto</span>
                            </div>
                            <div style={styles.chatPromptBottom}>
                                <input type="text" readOnly placeholder="Ask anything..." style={styles.chatInput} />
                                <button type="button" style={styles.sendButton} aria-label="Send">
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none"><path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z" fill="currentColor" /></svg>
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </Window>
    );
};

const styles: Record<string, React.CSSProperties> = {
    wrapper: {
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        backgroundColor: '#1e1e1e',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    },
    body: {
        flex: 1,
        display: 'flex',
        flexDirection: 'row',
        flexWrap: 'nowrap',
        minHeight: 0,
    },
    activityBar: {
        width: 48,
        backgroundColor: '#252526',
        borderRight: '1px solid rgba(255,255,255,0.06)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        paddingTop: 8,
        gap: 4,
        flexShrink: 0,
    },
    activityIcon: {
        width: 48,
        height: 40,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: 18,
        color: 'rgba(255,255,255,0.6)',
    },
    activityIconActive: {
        width: 48,
        height: 40,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: 18,
        color: 'rgba(255,255,255,0.95)',
        borderLeft: '2px solid rgba(255,255,255,0.8)',
        boxSizing: 'border-box',
    },
    sidebar: {
        width: 200,
        backgroundColor: '#252526',
        borderRight: '1px solid rgba(255,255,255,0.06)',
        display: 'flex',
        flexDirection: 'column',
        flexShrink: 0,
        minWidth: 0,
    },
    sidebarTabsRow: {
        height: 32,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 4,
        borderBottom: '1px solid rgba(255,255,255,0.06)',
        flexShrink: 0,
    },
    sidebarTabActive: {
        fontSize: 11,
        color: 'rgba(255,255,255,0.95)',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        fontWeight: 600,
        padding: '4px 10px',
        backgroundColor: '#1e1e1e',
        borderRadius: 4,
    },
    sidebarTabInactive: {
        fontSize: 11,
        color: 'rgba(255,255,255,0.5)',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        padding: '4px 8px',
    },
    fileTree: {
        paddingLeft: 6,
        paddingTop: 6,
        overflowY: 'auto',
        overflowX: 'hidden',
        flex: 1,
        minHeight: 0,
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        flexWrap: 'nowrap',
        alignItems: 'stretch',
    },
    fileTreeInner: {
        display: 'flex',
        flexDirection: 'column',
        flexWrap: 'nowrap',
        alignItems: 'stretch',
        width: '100%',
        minWidth: 0,
    },
    mainArea: {
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        minWidth: 0,
    },
    tabsRow: {
        height: 32,
        backgroundColor: '#252526',
        borderBottom: '1px solid rgba(255,255,255,0.06)',
        display: 'flex',
        alignItems: 'center',
        paddingLeft: 8,
        gap: 2,
        flexShrink: 0,
    },
    tabActive: {
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        gap: 6,
        paddingLeft: 10,
        paddingRight: 8,
        backgroundColor: '#1e1e1e',
        borderLeft: '1px solid rgba(255,255,255,0.06)',
        borderRight: '1px solid rgba(255,255,255,0.06)',
        fontSize: 12,
        color: 'rgba(255,255,255,0.9)',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    },
    tabInactive: {
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        gap: 6,
        paddingLeft: 10,
        paddingRight: 8,
        fontSize: 12,
        color: 'rgba(255,255,255,0.5)',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    },
    tabClose: {
        fontSize: 14,
        color: 'rgba(255,255,255,0.5)',
        lineHeight: 1,
    },
    contentPanel: {
        flex: 1,
        minHeight: 0,
        overflow: 'auto',
        backgroundColor: '#1e1e1e',
        padding: 12,
    },
    staticContent: {
        fontSize: 12,
        color: 'rgba(255,255,255,0.85)',
        lineHeight: 1.5,
    },
    terminalPanel: {
        height: 120,
        backgroundColor: '#1e1e1e',
        borderTop: '1px solid rgba(255,255,255,0.08)',
        display: 'flex',
        flexDirection: 'column',
        flexShrink: 0,
    },
    terminalHeader: {
        height: 28,
        backgroundColor: '#323233',
        fontSize: 11,
        color: '#ccc',
        paddingLeft: 12,
        paddingRight: 8,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    },
    terminalShellLabel: {
        marginLeft: 8,
        color: 'rgba(255,255,255,0.45)',
        fontSize: 10,
    },
    terminalHeaderRight: {
        display: 'flex',
        alignItems: 'center',
        gap: 4,
    },
    terminalHeaderBtn: {
        width: 24,
        height: 22,
        padding: 0,
        border: 'none',
        background: 'transparent',
        color: 'rgba(255,255,255,0.6)',
        fontSize: 14,
        cursor: 'default',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    },
    terminalHeaderBtnClose: {
        width: 26,
        height: 22,
        padding: 0,
        border: 'none',
        borderRadius: 4,
        background: '#c42b1c',
        color: '#fff',
        fontSize: 14,
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    },
    terminalCloseIcon: {
        display: 'block',
        lineHeight: 1,
    },
    terminalContent: {
        flex: 1,
        padding: 8,
        overflowY: 'auto',
        overflowX: 'hidden',
        fontSize: 12,
        fontFamily: 'ui-monospace, "SF Mono", Monaco, monospace',
        color: '#cccccc',
        display: 'block',
    },
    terminalLines: {
        display: 'block',
        width: '100%',
    },
    terminalLine: {
        display: 'block',
        lineHeight: 1.4,
    },
    chatPanel: {
        width: 280,
        backgroundColor: '#252526',
        borderLeft: '1px solid rgba(255,255,255,0.06)',
        display: 'flex',
        flexDirection: 'column',
        flexShrink: 0,
        minWidth: 0,
    },
    chatTopRow: {
        height: 36,
        paddingLeft: 12,
        paddingRight: 8,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        borderBottom: '1px solid rgba(255,255,255,0.06)',
        flexShrink: 0,
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    },
    chatTabs: {
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        flex: 1,
        minWidth: 0,
    },
    chatTabActive: {
        fontSize: 12,
        color: 'rgba(255,255,255,0.95)',
        fontWeight: 500,
    },
    chatTabInactive: {
        fontSize: 12,
        color: 'rgba(255,255,255,0.45)',
    },
    chatTopRight: {
        display: 'flex',
        alignItems: 'center',
        gap: 4,
    },
    chatTopBtn: {
        width: 24,
        height: 24,
        padding: 0,
        border: 'none',
        background: 'transparent',
        color: 'rgba(255,255,255,0.7)',
        fontSize: 14,
        cursor: 'default',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    },
    chatTopIcon: {
        width: 24,
        height: 24,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: 14,
        color: 'rgba(255,255,255,0.6)',
    },
    chatMessages: {
        flex: 1,
        overflowY: 'auto',
        padding: 12,
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        minHeight: 0,
    },
    chatBubble: {
        display: 'flex',
        gap: 10,
        marginBottom: 12,
    },
    chatBubbleContent: {
        flex: 1,
        minWidth: 0,
    },
    chatMessage: {
        fontSize: 13,
        color: 'rgba(255,255,255,0.9)',
        lineHeight: 1.5,
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    },
    chatEasterEgg: {
        fontSize: 11,
        color: '#6a6a6a',
        lineHeight: 1.4,
        margin: '0 0 12px 0',
        fontStyle: 'italic',
    },
    reviewTextRow: {
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        padding: '6px 12px',
        borderTop: '1px solid rgba(255,255,255,0.06)',
        backgroundColor: 'rgba(0,0,0,0.1)',
        flexShrink: 0,
    },
    reviewTextItem: {
        fontSize: 11,
        color: 'rgba(255,255,255,0.7)',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    },
    chatPromptContainer: {
        borderTop: '1px solid rgba(255,255,255,0.06)',
        padding: 10,
        backgroundColor: 'rgba(0,0,0,0.15)',
        width: '100%',
        boxSizing: 'border-box',
        flexShrink: 0,
        display: 'flex',
        flexDirection: 'column',
    },
    chatPromptTop: {
        display: 'flex',
        gap: 6,
        marginBottom: 8,
        flexShrink: 0,
    },
    chatModePillActive: {
        fontSize: 11,
        padding: '3px 8px',
        borderRadius: 4,
        backgroundColor: 'rgba(0,122,255,0.35)',
        color: 'rgba(255,255,255,0.95)',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    },
    chatModePill: {
        fontSize: 11,
        padding: '3px 8px',
        borderRadius: 4,
        color: 'rgba(255,255,255,0.5)',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    },
    chatPromptBottom: {
        display: 'flex',
        gap: 8,
        alignItems: 'center',
        width: '100%',
        boxSizing: 'border-box',
    },
    chatInput: {
        flex: 1,
        minWidth: 0,
        height: 32,
        backgroundColor: 'rgba(255,255,255,0.06)',
        border: '1px solid rgba(255,255,255,0.1)',
        borderRadius: 8,
        color: 'rgba(255,255,255,0.9)',
        padding: '0 12px',
        fontSize: 12,
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    },
    sendButton: {
        height: 32,
        width: 32,
        padding: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'rgba(255,255,255,0.12)',
        color: 'rgba(255,255,255,0.8)',
        border: 'none',
        borderRadius: 6,
        cursor: 'default',
    },
};

export default CursorAppWindow;
